import { EnumVesselMainPurpose } from "@/app/services/generated/enums/enumVesselMainPurpose.generated";
import type { Vessel } from "@/app/services/generated/vesselv2Service";
import {
  VesselFilter,
  VesselService,
} from "@/app/services/generated/vesselv2Service";
import type Guid from "@/app/types/common/guid";
import type { WithRequiredProperty } from "@/app/types/typeUtils";
import {
  distinctGuids,
  isNotNullOrUndefined,
  sumMap,
  toGroupedGuidMap,
  toGuidMap,
} from "@/app/apiutils";
import { recipeService as recipeV1Service } from "@/app/services/generated/recipeService";
import { vesselMaintenanceRecordService } from "@/app/services/generated/vesselmaintenancerecordService";
import { vesselMovementService } from "@/app/services/generated/vesselmovementService";
import type {
  MaintenanceInProgress,
  VesselSummary,
  VesselSummaryContents,
} from "@/components/pages/production/vessels/composables/vesselSummaries";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { Assembly } from "@/app/services/generated/assemblyService";
import { batchService } from "@/app/services/generated/batchService";
import { qcProbeService } from "@/app/services/generated/qcprobeService";
import { buildCache } from "@/app/caching/buildCache";

export type VesselWithCaskVessel = WithRequiredProperty<Vessel, "caskVessel">;
export type VesselWithProductionVessel = WithRequiredProperty<
  Vessel,
  "productionVessel"
>;

export default class VesselServiceExtended extends VesselService {
  async getAllCaskVessels(): Promise<VesselWithCaskVessel[]> {
    const filter = new VesselFilter();
    filter.limitMainPurpose = [EnumVesselMainPurpose.caskVessel];

    const filterResult = await this.getVesselsByFilter(filter);
    return filterResult.vessels as VesselWithCaskVessel[];
  }

  async getAllProductionVessels(): Promise<VesselWithProductionVessel[]> {
    const filter = new VesselFilter();
    filter.limitMainPurpose = [EnumVesselMainPurpose.productionVessel];

    const filterResult = await this.getVesselsByFilter(filter);
    return filterResult.vessels as VesselWithProductionVessel[];
  }

  async getVesselWithMainPurpose<
    T extends VesselWithCaskVessel | VesselWithProductionVessel,
  >(id: Guid, type: EnumVesselMainPurpose): Promise<T> {
    const filter = new VesselFilter();
    filter.limitMainPurpose = [type];

    const filterResult = await this.getVesselsByFilter(filter);
    const foundVessel = filterResult.vessels.find((vessel) =>
      vessel.id.equals(id)
    );
    if (foundVessel === undefined) {
      throw new Error(`Vessel with id ${id} not found`);
    }
    return foundVessel as T;
  }

  filterVesselsToMainPurpose<T>(
    vessels: Vessel[],
    type: EnumVesselMainPurpose
  ) {
    const filteredVessels = vessels.filter(
      (vessel) => vessel.mainPurpose === type
    );
    return filteredVessels as T[];
  }

  isCaskVessel(vessel: Readonly<Vessel>): vessel is VesselWithCaskVessel {
    return vessel.mainPurpose.isCaskVessel();
  }

  isProductionVessel(vessel: Vessel): vessel is VesselWithProductionVessel {
    return vessel.mainPurpose.isProductionVessel();
  }

  async getAssemblyVesselSummaries(
    vessels: readonly Vessel[],
    vesselAssemblies: readonly Assembly[]
  ) {
    const cache = buildCache()
      .withStockLocations()
      .withVesselTypes()
      .withVesselStates()
      .withItems()
      .withUnitsOfMeasure()
      .withRecipes()
      .build();

    const distinctVesselIds = distinctGuids(vessels.map((vessel) => vessel.id));

    const [
      recipeMap,
      vesselIdToVesselAvailabilityStateMap,
      vesselIdToIncompleteMaintenanceRecordMap,
      vesselIdToVesselContentVolumeMap,
      vesselIdToProbesMap,
    ] = await Promise.all([
      recipeV1Service
        .getRecipes(
          distinctGuids(
            vesselAssemblies.map((assembly) => assembly.header.recipeId)
          )
        )
        .then((recipes) => toGuidMap(recipes, (recipe) => recipe.header.id)),
      vesselService
        .areVesselsAvailable(distinctVesselIds)
        .then((states) => toGuidMap(states, (state) => state.vesselId)),
      vesselMaintenanceRecordService
        .getIncompleteMaintenanceRecordsForVessels(distinctVesselIds)
        .then((records) => toGuidMap(records, (record) => record.vesselId)),
      vesselMovementService
        .getContentVolumesOfVessels(distinctVesselIds)
        .then((volumes) => toGuidMap(volumes, (volume) => volume.vesselId)),
      qcProbeService.getAllQcProbes().then((probes) =>
        toGroupedGuidMap(
          probes.filter((probe) => probe.vesselId),
          (probe) => probe.vesselId!
        )
      ),
      cache.update(),
    ]);

    const vesselAssemblyMap = toGuidMap(
      vesselAssemblies,
      (assembly) => assembly.header.id
    );

    const getVesselSummaryContents = (
      assemblyId: Guid | null
    ): VesselSummaryContents | null => {
      if (!assemblyId) {
        return null;
      }

      const vesselAssembly = vesselAssemblyMap.get(assemblyId);

      if (
        !vesselAssembly ||
        !vesselAssembly.header.calculated.status.isInProgress() ||
        !vesselAssembly.header.readOnly.startedAt
      ) {
        return null;
      }

      const item = cache.items.getById(
        vesselAssembly.header.outputStillExpected?.stockItemId
      );

      const unitOfMeasure = cache.unitsOfMeasure.getById(
        item?.details.unitOfMeasureId
      );

      const recipe = vesselAssembly.header.recipeId
        ? (recipeMap.get(vesselAssembly.header.recipeId) ?? null)
        : null;

      return {
        outputStockItemName: item?.details.name ?? null,
        recipeName: recipe?.header.name ?? null,
        parentUrn: vesselAssembly.header.urn,
        parentExpectedEndDate:
          vesselAssembly.header.currentExpectedCompletionDate,
        parentName: vesselAssembly.header.name,
        parentId: vesselAssembly.header.id,
        parentStartDate: vesselAssembly.header.startDate,
        parentType: vesselAssembly.header.assemblyType,
        parentCurrentOutput:
          vesselAssembly.header.calculated.outputProduced?.quantity ?? null,
        parentRemainingExpectedOutput:
          vesselAssembly.header.outputStillExpected?.quantity ?? null,
        parentCostSoFar: vesselAssembly.header.readOnly.costSoFar,
        parentExpectedCost: vesselAssembly.header.readOnly.costExpectedAtStart,
        parentOutputUoMSuffix: unitOfMeasure?.name ?? null,
        batchId: null,
        produceMaterialName: null,
        processSubBatch: "",
      };
    };

    const vesselSummaries = vessels.map((vessel): VesselSummary => {
      const stockLocation = cache.stockLocations.getById(
        vessel.stockLocationId,
        true
      );

      const vesselAvailabilityState = vesselIdToVesselAvailabilityStateMap.get(
        vessel.id
      );

      const incompleteMaintenanceRecord =
        vesselIdToIncompleteMaintenanceRecordMap.get(vessel.id);

      const maintenanceInProgress: MaintenanceInProgress | undefined =
        incompleteMaintenanceRecord
          ? {
              vesselMaintenanceRecipeId:
                incompleteMaintenanceRecord.maintenanceRecipeId!,
              vesselMaintenanceRecipeName:
                cache.recipes.forId(
                  incompleteMaintenanceRecord.maintenanceRecipeId
                )?.header.name ?? "",
              vesselMaintenanceRecordId: incompleteMaintenanceRecord.id,
            }
          : undefined;

      const requiredMaintenanceRecipes =
        vesselAvailabilityState?.requiredMaintenanceRecipeId.map(
          (recipeId) => cache.recipes.forId(recipeId).header
        ) ?? [];

      const vesselAssembly = vessel.readOnly.currentAssemblyId
        ? (vesselAssemblyMap.get(vessel.readOnly.currentAssemblyId) ?? null)
        : null;

      const vesselDisplayColour = vessel.productionVessel?.displayColour || "";

      const vesselFillColour =
        vesselAssembly?.header.vesselFillColour || vesselDisplayColour;

      // We assume (confirmed by BE) that these will be sorted
      const assemblyProcessLines =
        vesselAssembly?.processGroups.flatMap((group) => group.lines) ?? [];

      // [...new Set()] gets Distinct elements.
      const qcProbeProviders = [
        ...new Set(
          (vesselIdToProbesMap.get(vessel.id) ?? []).map((x) => x.provider)
        ),
      ];

      return {
        vessel,
        vesselId: vessel.id,
        vesselStockLocationId: stockLocation.id,
        vesselStockLocationName: stockLocation.name,
        vesselSequence: vessel.productionVessel
          ? vessel.productionVessel.ordering
          : -1,
        vesselName: vessel.name,
        vesselShortName: vessel.shortName || null,
        vesselDisplayColour,
        vesselFillColour,
        vesselCapacityLitres: vessel.capacityLitres,
        requiresClean:
          !!vesselAvailabilityState?.availability.isRequiresMaintenance(),
        vesselType:
          cache.vesselTypes.getById(vessel.productionVessel?.vesselTypeId) ??
          null,
        vesselState: cache.vesselStates.getById(vessel.vesselStateId) ?? null,
        vesselCurrentLitres:
          vesselIdToVesselContentVolumeMap.get(vessel.id)?.currentLitres ?? 0,
        vesselContents: getVesselSummaryContents(
          vessel.readOnly.currentAssemblyId
        ),
        maintenanceInProgress,
        requiredMaintenanceRecipes,
        nextProcessLine: assemblyProcessLines.find((line) => !line.complete),
        vesselMainPurpose: vessel.mainPurpose,
        caskVessel: vessel.caskVessel,
        productionVessel: vessel.productionVessel,
        qcProbeProviders,
        vesselValue: null,
        blockingQcTestsInCurrentProcess: [],
      };
    });

    return vesselSummaries.sort((a, b) => a.vesselSequence - b.vesselSequence);
  }

  async getProcessVesselSummaries(vessels: readonly Vessel[]) {
    const cache = buildCache()
      .withStockLocations()
      .withVesselTypes()
      .withVesselStates()
      .withItems()
      .withUnitsOfMeasure()
      .withRecipes()
      .build();

    const distinctVesselIds = distinctGuids(vessels.map((vessel) => vessel.id));

    const [
      processMap,
      vesselIdToVesselAvailabilityStateMap,
      vesselIdToIncompleteMaintenanceRecordMap,
      vesselIdToVesselContentVolumeMap,
      vesselIdToProbesMap,
      vesselValuationsMap,
      processBlockingQcMap,
    ] = await Promise.all([
      batchService
        .getProcesses(
          distinctGuids(
            vessels.map((vessel) => vessel.readOnly.currentProcessId)
          )
        )
        .then((processes) =>
          toGuidMap(processes, (process) => process.header.id)
        ),
      vesselService
        .areVesselsAvailable(distinctVesselIds)
        .then((states) => toGuidMap(states, (state) => state.vesselId)),
      vesselMaintenanceRecordService
        .getIncompleteMaintenanceRecordsForVessels(distinctVesselIds)
        .then((records) => toGuidMap(records, (record) => record.vesselId)),
      vesselMovementService
        .getContentVolumesOfVessels(distinctVesselIds)
        .then((volumes) => toGuidMap(volumes, (volume) => volume.vesselId)),
      qcProbeService.getAllQcProbes().then((probes) =>
        toGroupedGuidMap(
          probes.filter((probe) => probe.vesselId),
          (probe) => probe.vesselId!
        )
      ),
      toGuidMap(
        await vesselService.getVesselsCurrentValue(distinctVesselIds),
        (x) => x.vesselId
      ),
      toGroupedGuidMap(
        await batchService.getBlockingQcTestsForProcesses(
          distinctGuids(
            vessels.map((vessel) => vessel.readOnly.currentProcessId)
          )
        ),
        (x) => x.processId
      ),
      cache.update(),
    ]);

    const batchHeaderMap = toGuidMap(
      await batchService.getBatchHeaders(
        distinctGuids(
          processMap.values().map((process) => process.header.batchId)
        )
      ),
      (header) => header.id
    );

    const getVesselSummaryContents = (
      processId: Guid | null
    ): VesselSummaryContents | null => {
      if (!processId) {
        return null;
      }

      const vesselProcess = processMap.get(processId);

      if (
        !vesselProcess ||
        !vesselProcess.header.calculated.status.isInProgress() ||
        !vesselProcess.header.readOnly.startedAt
      ) {
        return null;
      }

      const item = cache.items.getById(
        vesselProcess.header.outputStillExpected?.stockItemId ??
          vesselProcess.header.calculated.outputProduced?.stockItemId
      );

      const unitOfMeasure = cache.unitsOfMeasure.getById(
        item?.details.unitOfMeasureId
      );

      const recipe = cache.recipes.getById(
        vesselProcess.header.readOnly.recipeId
      );

      const batchHeader = batchHeaderMap.get(vesselProcess.header.batchId);

      return {
        outputStockItemName: item?.details.name ?? null,
        recipeName: recipe?.header.name ?? null,
        parentUrn: batchHeader?.urn ?? "",
        parentExpectedEndDate:
          vesselProcess.header.startDate?.plusDays(
            vesselProcess.header.expectedDurationDays
          ) ?? tenantService.today,
        parentName: vesselProcess.header.name,
        parentId: vesselProcess.header.id,
        parentStartDate: vesselProcess.header.startDate,
        parentType: vesselProcess.header.processType,
        parentCurrentOutput:
          vesselProcess.header.calculated.outputProduced?.quantity ?? null,
        parentRemainingExpectedOutput:
          vesselProcess.header.outputStillExpected?.quantity ?? null,
        parentCostSoFar: sumMap(
          vesselProcess.ingredients.flatMap((x) => x.usedLines),
          (x) => x.totalCost
        ),
        parentExpectedCost: vesselProcess.header.readOnly.costExpectedAtStart,
        parentOutputUoMSuffix: unitOfMeasure?.name ?? null,
        batchId: vesselProcess.header.batchId,
        produceMaterialName:
          cache.items.forId(batchHeader?.materialProducedStockItemId)?.details
            .name ?? null,
        processSubBatch: vesselProcess.header.subBatch,
      };
    };

    const vesselSummaries = vessels.map((vessel): VesselSummary => {
      const stockLocation = cache.stockLocations.getById(
        vessel.stockLocationId,
        true
      );

      const vesselAvailabilityState = vesselIdToVesselAvailabilityStateMap.get(
        vessel.id
      );

      const incompleteMaintenanceRecord =
        vesselIdToIncompleteMaintenanceRecordMap.get(vessel.id);

      const maintenanceInProgress: MaintenanceInProgress | undefined =
        incompleteMaintenanceRecord
          ? {
              vesselMaintenanceRecipeId:
                incompleteMaintenanceRecord.maintenanceRecipeId!,
              vesselMaintenanceRecipeName:
                cache.recipes.forId(
                  incompleteMaintenanceRecord.maintenanceRecipeId
                )?.header.name ?? "",
              vesselMaintenanceRecordId: incompleteMaintenanceRecord.id,
            }
          : undefined;

      const requiredMaintenanceRecipes =
        vesselAvailabilityState?.requiredMaintenanceRecipeId
          .map((recipeId) => cache.recipes.forId(recipeId).header)
          .filter(isNotNullOrUndefined) ?? [];

      const vesselProcess = vessel.readOnly.currentProcessId
        ? (processMap.get(vessel.readOnly.currentProcessId) ?? null)
        : null;

      const vesselBatchHeader = vesselProcess?.header.batchId
        ? (batchHeaderMap.get(vesselProcess?.header.batchId) ?? null)
        : null;

      const vesselDisplayColour = vessel.productionVessel?.displayColour || "";

      const vesselFillColour =
        vesselBatchHeader?.vesselFillColour || vesselDisplayColour;

      const processInstructions =
        vesselProcess?.instructions
          .slice()
          .sort((a, b) => a.sequence - b.sequence) ?? [];

      // [...new Set()] gets Distinct elements.
      const qcProbeProviders = [
        ...new Set(
          (vesselIdToProbesMap.get(vessel.id) ?? []).map((x) => x.provider)
        ),
      ];

      return {
        vessel,
        vesselId: vessel.id,
        vesselStockLocationId: stockLocation.id,
        vesselStockLocationName: stockLocation.name,
        vesselSequence: vessel.productionVessel
          ? vessel.productionVessel.ordering
          : -1,
        vesselName: vessel.name,
        vesselShortName: vessel.shortName || null,
        vesselDisplayColour,
        vesselFillColour,
        vesselCapacityLitres: vessel.capacityLitres,
        requiresClean:
          !!vesselAvailabilityState?.availability.isRequiresMaintenance(),
        vesselType:
          cache.vesselTypes.getById(vessel.productionVessel?.vesselTypeId) ??
          null,
        vesselState: cache.vesselStates.getById(vessel.vesselStateId) ?? null,
        vesselCurrentLitres:
          vesselIdToVesselContentVolumeMap.get(vessel.id)?.currentLitres ?? 0,
        vesselContents: getVesselSummaryContents(
          vessel.readOnly.currentProcessId
        ),
        maintenanceInProgress,
        requiredMaintenanceRecipes,
        nextInstructionLine: processInstructions.find(
          (instruction) => !instruction.complete
        ),
        vesselMainPurpose: vessel.mainPurpose,
        caskVessel: vessel.caskVessel,
        productionVessel: vessel.productionVessel,
        qcProbeProviders,
        vesselValue: vesselValuationsMap.get(vessel.id)?.value ?? null,
        blockingQcTestsInCurrentProcess:
          processBlockingQcMap.get(vesselProcess?.header.id) ?? [],
      };
    });

    return {
      vesselSummaries: vesselSummaries.sort(
        (a, b) => a.vesselSequence - b.vesselSequence
      ),
      processMap,
      batchHeaderMap,
    };
  }
}

export const vesselService = new VesselServiceExtended();
